// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// hooks
import { useTracking, TrackingEvent } from 'hooks/useTracking';
import { useGetByAccountQuery } from 'store/api/careCircle';
import { OrganiserCareCircle } from '@karehero/llama/pwa/pages/OrganiserPage/OrganiserPage';

// theme
import {
  WarmOrange,
  Cherry,
  LimeGreen,
  Lavender,
  Sunflower,
} from '@karehero/llama/theme/mui/color';

const ccColors = [
  WarmOrange[500],
  Cherry[500],
  LimeGreen[700],
  Lavender[500],
  Sunflower[500],
];

export const useOrganiserCareCircles = (options?: { skip?: boolean }) => {
  // Tracking
  const { track } = useTracking();

  const [activeCareCircleIds, setActiveCareCircleIds] = useState<string[]>([]);

  const { data: availableCareCircles, isLoading } = useGetByAccountQuery(
    undefined,
    {
      skip: options?.skip,
    },
  );

  const activeCareCircles = useMemo(
    () =>
      availableCareCircles?.filter((cc) =>
        activeCareCircleIds.find((id) => id === cc.id),
      ),
    [activeCareCircleIds, availableCareCircles],
  );

  const handleActiveCareCircleIdsChange = useCallback(
    (next: string[]) => {
      setActiveCareCircleIds(next);

      track(TrackingEvent.OrganiserCareCircleFilterChanged, {
        activeCircleCount: next.length,
        totalCircleCount: availableCareCircles?.length || 0,
      });
    },
    [availableCareCircles, track],
  );

  // Auto-activate all CCs on first load
  useEffect(() => {
    if (isLoading || !availableCareCircles || availableCareCircles.length === 0)
      return;

    if (activeCareCircleIds.length === 0) {
      setActiveCareCircleIds(availableCareCircles.map((cc) => cc.id));
    }
  }, [availableCareCircles, isLoading]);

  const careCircles: OrganiserCareCircle[] = useMemo(
    () =>
      availableCareCircles?.map((cc, i) => ({
        id: cc.id,
        name: cc.careRecipientAccount.fullName,
        color: ccColors[i % ccColors.length],
      })) || [],
    [availableCareCircles],
  );

  const activeCircles: OrganiserCareCircle[] = useMemo(
    () =>
      activeCareCircles?.map((cc, i) => ({
        id: cc.id,
        name: cc.careRecipientAccount.fullName,
        color: ccColors[i % ccColors.length],
      })) || [],
    [activeCareCircles],
  );

  return {
    isLoading,
    careCircles,
    activeCircles,
    activeCareCircleIds,
    handleActiveCareCircleIdsChange,
  };
};
